import React, { useState, useEffect } from 'react';
import { Organization, CreateOrganizationDto, UpdateOrganizationDto } from '../../types/organization';
import { listTaxonomies, TaxonomyItem } from '../../services/taxonomiesService';
import { KnownNamesField } from './KnownNamesField';
import { OrganizationTreeSelect } from './OrganizationTreeSelect';
import { InfluenceScoresEditor } from '../influences/InfluenceScoresEditor';
import { Button, Card, Field, Input, SectionHeader, Select, Textarea } from '../ui';

/** "religious-organization" → "Religious organization" */
const typeLabel = (type?: string) => {
  if (!type) return '';
  // Only reformat slugs; labels such as "Non-Governmental Organization (NGO)" stay as they are
  if (!/^[a-z0-9]+([-_][a-z0-9]+)*$/.test(type)) return type;
  const text = type.replace(/[-_]+/g, ' ');
  // Short single words are acronyms: "ngo" → "NGO"
  if (!text.includes(' ') && text.length <= 4) return text.toUpperCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

interface OrganizationFormProps {
  organization?: Organization;
  onSubmit: (data: CreateOrganizationDto | UpdateOrganizationDto) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  influenceEntityId?: string;
  influenceScores?: { categoryId: string; score: number }[];
  onInfluenceScoresChange?: (scores: { categoryId: string; score: number }[]) => void;
}

export const OrganizationForm: React.FC<OrganizationFormProps> = ({
  organization,
  onSubmit,
  onCancel,
  isLoading = false,
  influenceEntityId,
  influenceScores,
  onInfluenceScoresChange,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    acronym: '',
    description: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    parentId: ''
  });
  const [aliases, setAliases] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [orgTypes, setOrgTypes] = useState<TaxonomyItem[]>([]);

  useEffect(() => {
    (async () => {
      try { setOrgTypes(await listTaxonomies('organization_type')); } catch {}
    })();
  }, []);

  useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name || '',
        type: organization.type || '',
        acronym: organization.acronym || '',
        description: organization.description || '',
        email: organization.email || '',
        phone: organization.phone || '',
        address: organization.address || '',
        website: organization.website || '',
        parentId: organization.parentId || ''
      });
      setAliases(organization.aliases || []);
    }
  }, [organization]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Organization name is required';
    }

    if (!formData.type.trim()) {
      newErrors.type = 'Organization type is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.phone && !/^[+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/[\s\-()]/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (formData.website && !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(formData.website)) {
      newErrors.website = 'Please enter a valid website URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      acronym: formData.acronym.trim() || null,
      aliases,
      email: formData.email || undefined,
      phone: formData.phone || undefined,
      address: formData.address || undefined,
      website: formData.website || undefined,
      description: formData.description || undefined,
      parentId: formData.parentId === '' ? null : formData.parentId
    };

    try {
      await onSubmit(submitData);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleParentSelect = (parentId: string | null) => {
    setFormData(prev => ({ ...prev, parentId: parentId || '' }));
    if (errors.parentId) {
      setErrors(prev => ({ ...prev, parentId: '' }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 font-ui">
      <section>
        <SectionHeader title="Basics" />
        <Card padded className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Name" htmlFor="name" required error={errors.name}>
            <Input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              aria-invalid={!!errors.name}
              autoComplete="organization"
            />
          </Field>

          <Field label="Type" htmlFor="type" required error={errors.type}>
            <Select id="type" name="type" value={formData.type} onChange={handleChange} aria-invalid={!!errors.type}>
              <option value="">Select</option>
              {orgTypes.map((t) => (
                <option key={t.id} value={t.label}>
                  {t.label}
                </option>
              ))}
              {/* Keep a saved type visible when it is not in the taxonomy */}
              {formData.type && !orgTypes.some((t) => t.label === formData.type) && (
                <option value={formData.type}>{typeLabel(formData.type)}</option>
              )}
            </Select>
          </Field>

          <Field label="Acronym" htmlFor="acronym" error={errors.acronym}>
            <Input
              id="acronym"
              name="acronym"
              type="text"
              value={formData.acronym}
              onChange={handleChange}
              placeholder="SOGUIPAMI"
              maxLength={40}
            />
          </Field>

          <KnownNamesField
            value={aliases}
            onChange={setAliases}
            name={formData.name}
            className="sm:col-span-2"
          />

          <Field label="Parent" error={errors.parentId} className="sm:col-span-2">
            <OrganizationTreeSelect
              selectedId={formData.parentId}
              onSelect={handleParentSelect}
              excludeId={organization?.id}
              placeholder="None"
            />
          </Field>
        </Card>
      </section>

      <section>
        <SectionHeader title="Contact" />
        <Card padded className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Email" htmlFor="email" error={errors.email}>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              aria-invalid={!!errors.email}
              autoComplete="email"
            />
          </Field>

          <Field label="Phone" htmlFor="phone" error={errors.phone}>
            <Input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              aria-invalid={!!errors.phone}
              autoComplete="tel"
            />
          </Field>

          <Field label="Website" htmlFor="website" error={errors.website} className="sm:col-span-2">
            <Input
              type="url"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleChange}
              aria-invalid={!!errors.website}
              placeholder="https://"
              autoComplete="url"
            />
          </Field>

          <Field label="Address" htmlFor="address" className="sm:col-span-2">
            <Textarea
              id="address"
              name="address"
              rows={2}
              value={formData.address}
              onChange={handleChange}
              autoComplete="street-address"
            />
          </Field>
        </Card>
      </section>

      <section>
        <SectionHeader title="About" />
        <Card padded>
          <Field label="Description" htmlFor="description">
            <Textarea
              id="description"
              name="description"
              rows={4}
              value={formData.description}
              onChange={handleChange}
            />
          </Field>
        </Card>
      </section>

      {/* Influence by type */}
      <section>
        <SectionHeader title="Influence" />
        <Card padded>
          <InfluenceScoresEditor
            entityType="organization"
            entityId={influenceEntityId}
            onChange={(scores) => onInfluenceScoresChange?.(scores)}
          />
        </Card>
      </section>

      {/* Actions: a bar above the tab bar on phones, inline from sm up */}
      <div
        className="fixed inset-x-0 z-30 flex items-center gap-2 border-t border-neutral-200/70 bg-white/90 px-4 py-3 backdrop-blur-md sm:static sm:z-auto sm:justify-end sm:border-0 sm:bg-transparent sm:p-0 sm:backdrop-blur-none"
        style={{ bottom: 'var(--app-tabbar-h, 0px)' }}
      >
        <Button variant="secondary" onClick={onCancel} disabled={isLoading} className="flex-1 sm:flex-none">
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={isLoading} className="flex-1 sm:flex-none">
          {organization ? (isLoading ? 'Saving' : 'Save') : isLoading ? 'Creating' : 'Create'}
        </Button>
      </div>
      <div className="h-12 sm:hidden" aria-hidden="true" />
    </form>
  );
};
